import { useState } from 'react';
import { Link2Off, Monitor, Plus, QrCode } from 'lucide-react';
import { Badge, Button, StatusDot, Card, SectionHeader, EmptyState, ErrorState, LoadingState } from '@pixlide/ui';
import { formatRelative } from '@/lib/format';
import { useScreens, useSubscription } from '@/api/queries/dashboard';
import { useEstablishments } from '@/api/queries/settings';
import { useStartScreenPairing, useUnpairScreen, type PairingCode } from '@/api/queries/screens';
import { useHasPermission } from '@/stores/auth-store';
import { DeclareScreenPanel } from '@/features/screens/DeclareScreenPanel';
import { PairingPanel } from '@/features/screens/PairingPanel';
import { describeScreenError } from '@/features/screens/screen-errors';

/** Libellés des modes de diffusion, tels qu'énumérés côté API. */
const PLAYBACK_MODE_LABELS: Record<number, string> = {
  0: 'TV connectée',
  1: 'Boîtier Pixlide',
};

/** Rafraîchissement du parc pendant un appairage : l'écran doit apparaître « appairé » vite. */
const PAIRING_REFRESH_MS = 3_000;

interface PairingSession {
  readonly screenId: string;
  readonly screenName: string;
  readonly code: PairingCode;
}

/**
 * Parc d'écrans du compte : déclaration, appairage et détachement des appareils.
 *
 * La consommation de quota est rappelée en tête : c'est la contrainte qui bloque le client
 * au moment le plus gênant (installation d'un écran en salle), autant qu'elle soit visible
 * avant qu'il ne s'y heurte.
 */
export function ScreensPage() {
  const [isDeclaring, setIsDeclaring] = useState(false);
  const [pairing, setPairing] = useState<PairingSession | null>(null);
  const [confirmingUnpair, setConfirmingUnpair] = useState<string | null>(null);

  const screens = useScreens({}, pairing === null ? 60_000 : PAIRING_REFRESH_MS);
  const subscription = useSubscription();
  const establishments = useEstablishments();
  const startPairing = useStartScreenPairing();
  const unpair = useUnpairScreen();

  // Les boutons reflètent les permissions, mais c'est l'API qui les fait respecter : un
  // bouton masqué n'est qu'un confort, jamais une protection.
  // Hooks appelés inconditionnellement : un `&&` entre deux hooks sauterait le second.
  const canWriteScreens = useHasPermission('screens:write');
  const canWriteEstablishments = useHasPermission('establishments:write');
  const canPair = useHasPermission('screens:pair');
  const canDeclare = canWriteScreens && canWriteEstablishments;

  const pairedScreen = pairing === null ? undefined : screens.data?.items.find((screen) => screen.id === pairing.screenId);

  const openPairing = async (screen: { id: string; name: string }) => {
    setIsDeclaring(false);

    try {
      const code = await startPairing.mutateAsync(screen.id);
      setPairing({ screenId: screen.id, screenName: screen.name, code });
    } catch {
      // Affiché via startPairing.error.
    }
  };

  const regenerate = async () => {
    if (pairing === null) {
      return;
    }

    try {
      const code = await startPairing.mutateAsync(pairing.screenId);
      setPairing({ ...pairing, code });
    } catch {
      // Affiché via startPairing.error.
    }
  };

  const confirmUnpair = async (screenId: string) => {
    try {
      await unpair.mutateAsync(screenId);
    } finally {
      setConfirmingUnpair(null);
    }
  };

  const actionError = startPairing.error ?? unpair.error;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <SectionHeader
        title="Mes écrans"
        description={
          subscription.data
            ? `${subscription.data.activeScreens} écran(s) actif(s) sur ${subscription.data.screenAllowance} autorisé(s) par votre formule ${subscription.data.planName}.`
            : 'Écrans et boîtiers rattachés à votre compte.'
        }
        action={
          canDeclare && !isDeclaring ? (
            <Button
              onClick={() => {
                setPairing(null);
                setIsDeclaring(true);
              }}
            >
              <Plus aria-hidden className="size-4" />
              Déclarer un écran
            </Button>
          ) : undefined
        }
      />

      {isDeclaring && establishments.data ? (
        <DeclareScreenPanel
          establishments={establishments.data.items}
          onCancel={() => setIsDeclaring(false)}
          onDeclared={(screen, code) => {
            setIsDeclaring(false);
            setPairing({ screenId: screen.id, screenName: screen.name, code });
          }}
        />
      ) : null}

      {pairing !== null ? (
        <PairingPanel
          screenName={pairing.screenName}
          code={pairing.code}
          isPaired={pairedScreen?.isPaired === true}
          isRegenerating={startPairing.isPending}
          onRegenerate={() => void regenerate()}
          onClose={() => setPairing(null)}
        />
      ) : null}

      {actionError ? (
        <p role="alert" className="rounded-xl bg-danger-100 px-4 py-3 text-sm font-medium text-danger-500">
          {describeScreenError(actionError)}
        </p>
      ) : null}

      {screens.isPending ? <LoadingState label="Chargement du parc d'écrans…" /> : null}

      {screens.isError ? (
        <ErrorState
          message="Impossible de charger vos écrans pour le moment."
          onRetry={() => void screens.refetch()}
        />
      ) : null}

      {screens.data?.items.length === 0 && !isDeclaring && pairing === null ? (
        <EmptyState
          icon={<Monitor className="size-10" strokeWidth={1.25} />}
          title="Aucun écran déclaré"
          description="Déclarez un écran, puis saisissez sur votre TV ou votre boîtier Pixlide le code d'appairage affiché ici. Le code reste valable 15 minutes."
          action={
            canDeclare ? (
              <Button onClick={() => setIsDeclaring(true)}>
                <Plus aria-hidden className="size-4" />
                Déclarer mon premier écran
              </Button>
            ) : undefined
          }
        />
      ) : null}

      {screens.data && screens.data.items.length > 0 ? (
        <Card className="overflow-hidden">
          {/* Un vrai <table> et non une grille de <div> : les lecteurs d'écran annoncent
              alors les en-têtes de colonne à chaque cellule, ce qui rend un tableau de
              données réellement navigable. */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Liste des écrans du compte et de leur état</caption>

              <thead className="bg-ink-50/60 text-left text-xs font-medium tracking-wide text-ink-500 uppercase">
                <tr>
                  <th scope="col" className="px-5 py-3">Écran</th>
                  <th scope="col" className="px-5 py-3">Établissement</th>
                  <th scope="col" className="px-5 py-3">Diffusion</th>
                  <th scope="col" className="px-5 py-3">Résolution</th>
                  <th scope="col" className="px-5 py-3">État</th>
                  {canPair ? (
                    <th scope="col" className="px-5 py-3 text-right">
                      <span className="sr-only">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>

              <tbody className="divide-y divide-ink-100/70">
                {screens.data.items.map((screen) => (
                  <tr key={screen.id}>
                    <th scope="row" className="px-5 py-4 text-left font-medium">
                      <div className="flex items-center gap-2">
                        {screen.name}
                        {screen.isAvailableForAdvertising ? (
                          <Badge tone="brand">Régie</Badge>
                        ) : null}
                      </div>
                    </th>

                    <td className="px-5 py-4 text-ink-500">{screen.establishmentName}</td>

                    <td className="px-5 py-4 text-ink-500">
                      {/* Le schéma OpenAPI expose l'énumération en valeur numérique :
                          0 = TV connectée, 1 = boîtier player Pixlide. La correspondance est
                          faite ici plutôt que dispersée dans les composants. */}
                      {PLAYBACK_MODE_LABELS[screen.playbackMode] ?? 'Inconnu'}
                    </td>

                    <td className="px-5 py-4 text-ink-500">{screen.resolution}</td>

                    <td className="px-5 py-4">
                      {screen.isPaired ? (
                        <div className="flex flex-col gap-0.5">
                          <StatusDot online={screen.isOnline} />
                          {screen.lastHeartbeatAt ? (
                            <span className="text-xs text-ink-300">
                              vu {formatRelative(screen.lastHeartbeatAt)}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <Badge tone="warning">À appairer</Badge>
                      )}
                    </td>

                    {canPair ? (
                      <td className="px-5 py-4 text-right whitespace-nowrap">
                        {!screen.isPaired ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            isLoading={startPairing.isPending && startPairing.variables === screen.id && pairing === null}
                            onClick={() => void openPairing(screen)}
                          >
                            <QrCode aria-hidden className="size-4" />
                            Appairer
                          </Button>
                        ) : confirmingUnpair === screen.id ? (
                          // Confirmation en deux temps : détacher coupe la diffusion de cet
                          // écran immédiatement, et le rattacher demande quelqu'un sur place.
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="danger"
                              isLoading={unpair.isPending}
                              onClick={() => void confirmUnpair(screen.id)}
                            >
                              Confirmer le détachement
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setConfirmingUnpair(null)}>
                              Annuler
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            aria-label={`Détacher l'appareil de ${screen.name}`}
                            onClick={() => setConfirmingUnpair(screen.id)}
                          >
                            <Link2Off aria-hidden className="size-4" />
                            Détacher
                          </Button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
